import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Cake, UserCheck, CreditCard, Search, History, Zap, ReceiptText } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { PageLoader, LoadError, EmptyState } from "@/components/admin/LoadState";
import { Pager } from "@/components/admin/Pager";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useDebounced } from "@/hooks/useDebounced";
import { fmtNum, fmtDateTime } from "@/lib/format";

const EVENT_ICON: Record<string, React.ComponentType<any>> = { cumpleanos: Cake, registro: UserCheck, plan_pagado: CreditCard, pago_puntual: ReceiptText };
const EVENT_HELP: Record<string, string> = {
  cumpleanos: "Se revisa todos los días. Cada cliente lo recibe una sola vez al año por regla.",
  registro: "Se entrega al aprobar el registro del cliente, una sola vez. Un registro rechazado no lo recibe.",
  plan_pagado: "Se entrega cuando el pago está confirmado: con tarjeta, al comprar o mejorar; con débito automático, cuando Armony confirma el cobro.",
  pago_puntual: "Se entrega por cada factura de Odoo pagada hasta su vencimiento (más la tolerancia de la integración). Solo con la integración con Odoo en modo activo.",
};
const INTERVAL: Record<string, string> = { month: "Mensual", year: "Anual" };
const DEFAULT_CONDITIONS: Record<string, any> = {
  cumpleanos: { momento: "mes", niveles: null, solo_al_dia: false },
  registro: { solo_al_dia: false },
  plan_pagado: { movimientos: ["alta"], planes: null, modalidades: null, una_vez: true, solo_al_dia: false },
  pago_puntual: { solo_al_dia: false },
};

// Resumen en palabras de las condiciones de una regla (lo que verá el equipo en la tarjeta).
function describe(rule: any, meta: any): string[] {
  const c = rule.conditions || {};
  const out: string[] = [];
  if (rule.event === "cumpleanos") {
    out.push(c.momento === "dia" ? "El día de su cumpleaños" : "En su mes de cumpleaños");
    out.push(c.niveles ? `Niveles: ${c.niveles.join(", ")}` : "Todos los niveles");
  }
  if (rule.event === "plan_pagado") {
    out.push((c.movimientos || ["alta"]).map((m: string) => meta.moves.find((x: any) => x.key === m)?.label || m).join(" o "));
    out.push(c.planes ? c.planes.map((p: string) => meta.plans.find((x: any) => x.id === p)?.name || p).join(", ") : "Cualquier plan");
    if (c.modalidades) out.push(c.modalidades.map((m: string) => INTERVAL[m]).join(" o "));
    out.push(c.una_vez === false ? "Cada vez que ocurra" : "Una sola vez por cliente");
  }
  if (c.solo_al_dia) out.push("Solo clientes al día");
  return out;
}

export default function AdminAutoRules() {
  usePageTitle("Reglas automáticas · Puntos");
  const [data, setData] = useState<any>(null);
  const [failed, setFailed] = useState(false);
  const [form, setForm] = useState<any>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState<any>(null);

  const load = () => {
    setFailed(false);
    return api.get("/admin/auto-rules").then((r) => setData(r.data)).catch(() => setFailed(true));
  };
  useEffect(() => { load(); }, []);

  const open = (rule?: any, event?: string) => {
    setErrors({});
    setForm(rule ? JSON.parse(JSON.stringify(rule)) : { event, name: "", description: "", points: 100, active: true, conditions: { ...DEFAULT_CONDITIONS[event!] } });
  };
  const cond = (k: string, v: any) => setForm({ ...form, conditions: { ...form.conditions, [k]: v } });
  // Lista con «todos» = null: marcar todo vuelve a null.
  const toggleIn = (k: string, value: string, all: string[]) => {
    const cur: string[] = form.conditions[k] ?? all;
    const next = cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value];
    cond(k, next.length === all.length ? null : next);
  };

  const save = async () => {
    const e: Record<string, string> = {};
    if (!form.name?.trim()) e.name = "Escribe un nombre: lo verá el cliente en su historial de puntos.";
    const pts = Number(form.points);
    if (!Number.isInteger(pts) || pts < 0) e.points = "Los puntos deben ser un número entero.";
    else if (form.active && pts < 1) e.points = "Para activar la regla, los puntos deben ser mayores a 0.";
    for (const k of ["niveles", "planes", "modalidades", "movimientos"]) {
      if (Array.isArray(form.conditions[k]) && form.conditions[k].length === 0) e[k] = "Elige al menos una opción.";
    }
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      const payload = { event: form.event, name: form.name.trim(), description: form.description || null, points: pts, active: !!form.active, conditions: form.conditions };
      if (form.id) await api.put(`/admin/auto-rules/${form.id}`, payload);
      else await api.post("/admin/auto-rules", payload);
      toast.success(payload.active ? "Regla guardada: se aplicará en el próximo evento." : "Regla guardada como inactiva: no entregará puntos.");
      setForm(null); load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (rule: any) => {
    try {
      await api.put(`/admin/auto-rules/${rule.id}`, { ...rule, active: !rule.active });
      toast.success(rule.active ? "Regla pausada" : "Regla activada");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async () => {
    try { await api.delete(`/admin/auto-rules/${toDelete.id}`); toast.success("Regla eliminada"); load(); }
    catch (err) { toast.error(apiError(err)); throw err; }
  };

  if (failed) return <LoadError what="las reglas automáticas" onRetry={load} />;
  if (!data) return <PageLoader />;
  const err = (k: string) => errors[k] && <p role="alert" className="text-xs text-red-700 mt-1">{errors[k]}</p>;

  return (
    <div className="space-y-6" data-testid="auto-rules-page">
      {data.events.map((ev: any) => {
        const Icon = EVENT_ICON[ev.key] || Zap;
        const rules = data.rules.filter((r: any) => r.event === ev.key);
        return (
          <section key={ev.key} className="card-obsidian p-5 space-y-4" aria-labelledby={`ev-${ev.key}`} data-testid={`auto-event-${ev.key}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-full bg-navy/10 text-navy flex items-center justify-center shrink-0"><Icon className="w-5 h-5" aria-hidden="true" /></span>
                <div>
                  <h2 id={`ev-${ev.key}`} className="text-lg font-semibold">{ev.label}</h2>
                  <p className="text-sm text-slate-600 max-w-2xl">{EVENT_HELP[ev.key]}</p>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => open(undefined, ev.key)} className="rounded-full border-slate-200 text-slate-700" data-testid={`auto-new-${ev.key}`}>
                <Plus className="w-4 h-4 mr-1" aria-hidden="true" /> Otra regla
              </Button>
            </div>
            {rules.length === 0 ? (
              <p className="text-sm text-slate-600">No hay reglas para este evento: no se entregan puntos.</p>
            ) : (
              <ul className="grid md:grid-cols-2 gap-3">
                {rules.map((r: any) => (
                  <li key={r.id} className={`rounded-xl border p-4 ${r.active ? "border-slate-200" : "border-dashed border-slate-300 bg-slate-50"}`} data-testid="auto-rule-card">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-slate-900">{r.name}</p>
                        <p className="text-xs text-slate-600">{r.active ? "Activa" : "Pausada"} · {fmtNum(r.granted)} {r.granted === 1 ? "entrega" : "entregas"}</p>
                      </div>
                      <span className="text-gold-ink font-mono font-semibold">+{fmtNum(r.points)}</span>
                    </div>
                    <ul className="flex flex-wrap gap-1.5 mt-3 text-xs">
                      {describe(r, data).map((t) => <li key={t} className="bg-navy/10 text-navy rounded-full px-2 py-0.5 font-medium">{t}</li>)}
                    </ul>
                    <div className="flex items-center gap-2 mt-4">
                      <Switch checked={!!r.active} onCheckedChange={() => toggleActive(r)} aria-label={`${r.active ? "Pausar" : "Activar"} ${r.name}`} disabled={!r.active && r.points < 1} />
                      <span className="text-xs text-slate-600 flex-1">{r.active ? "Entregando" : r.points < 1 ? "Configura los puntos para activarla" : "Pausada"}</span>
                      <Button size="sm" variant="outline" onClick={() => open(r)} className="h-8 border-slate-200 text-slate-700"><Pencil className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Editar</Button>
                      {r.granted === 0 && (
                        <Button size="sm" variant="outline" onClick={() => setToDelete(r)} aria-label={`Eliminar ${r.name}`} className="h-8 border-red-500/30 text-red-700 hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5" aria-hidden="true" /></Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
      <p className="text-xs text-slate-600">
        Las entregas automáticas quedan en el <Link to="/admin/puntos/automaticas/historial" className="text-navy underline">historial automático</Link>, separadas de las entregas manuales. Un mismo evento nunca entrega dos veces los puntos de una regla.
      </p>

      <Dialog open={!!form} onOpenChange={(o: boolean) => !o && !saving && setForm(null)}>
        <DialogContent className="bg-obsidian-card border-slate-200 text-slate-900 sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="auto-rule-dialog">
          {form && <>
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">{form.id ? "Editar regla" : "Nueva regla"} · {data.events.find((e: any) => e.key === form.event)?.label}</DialogTitle>
              <DialogDescription className="text-slate-600">{EVENT_HELP[form.event]}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="ar-name" className="text-sm text-slate-700">Nombre * (lo ve el cliente en su historial)</Label>
                <Input id="ar-name" data-testid="auto-rule-name" value={form.name} onChange={(e: any) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
                {err("name")}
              </div>
              <div>
                <Label htmlFor="ar-points" className="text-sm text-slate-700">Puntos *</Label>
                <Input id="ar-points" data-testid="auto-rule-points" type="number" min={0} value={form.points} onChange={(e: any) => setForm({ ...form, points: e.target.value })} className="mt-1.5 w-40" />
                {err("points")}
              </div>
              <div>
                <Label htmlFor="ar-desc" className="text-sm text-slate-700">Descripción (la ven los clientes en «Cómo ganar puntos»)</Label>
                <Textarea id="ar-desc" rows={2} maxLength={500} value={form.description || ""} onChange={(e: any) => setForm({ ...form, description: e.target.value })} className="mt-1.5" />
              </div>

              {form.event === "cumpleanos" && <>
                <fieldset>
                  <legend className="text-sm text-slate-700 mb-1.5">¿Cuándo se entrega?</legend>
                  {[["mes", "En su mes de cumpleaños (desde el día 1)"], ["dia", "El día de su cumpleaños"]].map(([v, l]) => (
                    <label key={v} className="flex items-center gap-2 text-sm text-slate-700 py-0.5">
                      <input type="radio" name="momento" checked={form.conditions.momento === v} onChange={() => cond("momento", v)} className="accent-navy" /> {l}
                    </label>
                  ))}
                </fieldset>
                <Checks label="Niveles" k="niveles" all={data.tiers} labelOf={(t: string) => t} form={form} toggle={toggleIn} />
                {err("niveles")}
              </>}

              {form.event === "plan_pagado" && <>
                <Checks label="¿Cuándo se entrega?" k="movimientos" all={data.moves.map((m: any) => m.key)} labelOf={(k: string) => data.moves.find((m: any) => m.key === k)?.label} form={form} toggle={toggleIn} required />
                {err("movimientos")}
                <Checks label="Planes" k="planes" all={data.plans.map((p: any) => p.id)} labelOf={(k: string) => data.plans.find((p: any) => p.id === k)?.name} form={form} toggle={toggleIn} />
                {err("planes")}
                <Checks label="Modalidad" k="modalidades" all={["month", "year"]} labelOf={(k: string) => INTERVAL[k]} form={form} toggle={toggleIn} />
                {err("modalidades")}
                <label className="flex items-start gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={form.conditions.una_vez !== false} onChange={(e) => cond("una_vez", e.target.checked)} className="mt-0.5 accent-navy" data-testid="auto-rule-once" />
                  <span>Una sola vez por cliente<span className="block text-xs text-slate-600">Si lo desmarcas, se entrega cada vez que contrate o mejore (por ejemplo, si cancela y vuelve a contratar).</span></span>
                </label>
              </>}

              <label className="flex items-start gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={!!form.conditions.solo_al_dia} onChange={(e) => cond("solo_al_dia", e.target.checked)} className="mt-0.5 accent-navy" />
                <span>Solo clientes al día<span className="block text-xs text-slate-600">Los clientes inhabilitados nunca reciben puntos automáticos.</span></span>
              </label>
              <div className="flex items-center gap-2">
                <Switch id="ar-active" checked={!!form.active} onCheckedChange={(v: boolean) => setForm({ ...form, active: v })} />
                <Label htmlFor="ar-active" className="text-sm text-slate-700">Activa</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setForm(null)} disabled={saving} className="border-slate-200 text-slate-700 rounded-full">Cancelar</Button>
              <Button onClick={save} disabled={saving} data-testid="auto-rule-save" className="gold-gradient-bg text-white font-semibold rounded-full">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Guardando" /> : "Guardar"}
              </Button>
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        danger
        title={`¿Eliminar la regla “${toDelete?.name || ""}”?`}
        description={<p>Todavía no entregó puntos. Si solo quieres pausarla, usa el interruptor.</p>}
        confirmLabel="Eliminar regla"
        onConfirm={remove}
      />
    </div>
  );
}

function Checks({ label, k, all, labelOf, form, toggle, required }: any) {
  const cur: string[] = form.conditions[k] ?? all;
  return (
    <fieldset>
      <legend className="text-sm text-slate-700 mb-1.5">{label}{!required && <span className="text-xs text-slate-600"> · todo marcado = todos</span>}</legend>
      <div className="flex flex-wrap gap-2">
        {all.map((v: string) => (
          <label key={v} className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border cursor-pointer ${cur.includes(v) ? "border-navy bg-navy/5 text-navy" : "border-slate-200 text-slate-700"}`}>
            <input type="checkbox" checked={cur.includes(v)} onChange={() => toggle(k, v, all)} className="accent-navy" /> {labelOf(v)}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function AutoGrantsHistoryPage() {
  usePageTitle("Historial automático · Puntos");
  const [q, setQ] = useState("");
  const dq = useDebounced(q, 300);
  const [event, setEvent] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<any>(null);
  const [failed, setFailed] = useState(false);

  const load = () => {
    setFailed(false);
    api.get("/admin/auto-grants", { params: { q: dq, page, page_size: 25, ...(event && { event }) } }).then((r) => setData(r.data)).catch(() => setFailed(true));
  };
  useEffect(() => { setPage(1); }, [dq, event]);
  useEffect(load, [dq, page, event]); // eslint-disable-line

  return (
    <div className="space-y-4" data-testid="auto-grants-history">
      <div className="flex flex-wrap gap-3">
        <div className="relative max-w-md flex-1 min-w-[16rem]">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" />
          <Input aria-label="Buscar en el historial automático" value={q} onChange={(e: any) => setQ(e.target.value)} placeholder="Buscar por cliente, cédula o regla" className="pl-9 h-11" />
        </div>
        <select aria-label="Evento" value={event} onChange={(e) => setEvent(e.target.value)} className="h-11 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="auto-grants-event">
          <option value="">Todos los eventos</option>
          <option value="cumpleanos">Cumpleaños</option>
          <option value="registro">Registro aprobado</option>
          <option value="plan_pagado">Plan pagado</option>
          <option value="pago_puntual">Pago puntual (Odoo)</option>
        </select>
      </div>
      {failed ? <LoadError what="el historial automático" onRetry={load} /> : !data ? <PageLoader /> : data.items.length === 0 ? (
        <EmptyState icon={History}>{dq || event ? "No hay entregas automáticas con esos filtros." : "Todavía no hay entregas automáticas."}</EmptyState>
      ) : (
        <div className="card-obsidian overflow-hidden">
          <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Historial de entregas automáticas">
            <table className="w-full text-sm min-w-[860px]">
              <thead>
                <tr className="border-b border-slate-200 text-slate-600 text-xs uppercase tracking-wider">
                  <th scope="col" className="text-left font-medium px-4 py-3">Fecha</th>
                  <th scope="col" className="text-left font-medium px-4 py-3">Cliente</th>
                  <th scope="col" className="text-right font-medium px-4 py-3">Puntos</th>
                  <th scope="col" className="text-left font-medium px-4 py-3">Regla</th>
                  <th scope="col" className="text-left font-medium px-4 py-3">Origen</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((g: any) => (
                  <tr key={g.id} className="border-b border-slate-200" data-testid="auto-grant-row">
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDateTime(g.created_at)}</td>
                    <td className="px-4 py-3">
                      <Link to={`/admin/usuarios/${g.user_id}`} className="text-slate-900 font-medium hover:underline">{g.user_name}</Link>
                      <p className="text-xs text-slate-600 font-mono">{g.user_cedula || "—"}</p>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-emerald-800">+{fmtNum(g.points)}</td>
                    <td className="px-4 py-3 text-slate-700">{g.rule_name}<p className="text-xs text-slate-600">{g.event_label}</p></td>
                    <td className="px-4 py-3 text-slate-700">
                      {g.source_label}
                      <p className="text-xs text-slate-600">
                        {g.detail?.movimiento ? `${g.detail.movimiento === "alta" ? "Alta" : "Mejora"} · ` : ""}
                        {g.detail?.aprobado_por ? `Aprobó ${g.detail.aprobado_por}` : ""}
                        {g.source_ref ? <span className="font-mono"> {g.source_ref.slice(0, 10)}</span> : ""}
                      </p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={data.page} pageSize={data.page_size} total={data.total} onPage={setPage} />
        </div>
      )}
    </div>
  );
}
